import { JobType } from "@app/jobs";
import { sortBy } from "lodash";
import { createContext, JSX, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import JobPerformanceService from "./jobPerformanceService";
import {
  PredefinedJobChartStorage,
  PreDefinedJobStatisticsChart,
  GraphTypeEnum,
  UserDefinedJobChartStorage,
  UserDefinedChartModel,
  ChartColumnEnum,
  UserDefinedChartParameters,
} from "./types";

interface JobStatisticsContextProps {
  predefinedCharts: PredefinedJobChartStorage;
  refreshAllPredefinedCharts: () => void;
  refreshPredefinedChart: (jobType: JobType, chartType: GraphTypeEnum) => void;

  refreshUserDefinedChart: (chartId: number) => void;
  deleteUserDefinedChart: (chartId: number) => void;
  userDefinedCharts: UserDefinedJobChartStorage;

  getJobTypes: () => Promise<JobType[]>;
  getXAxisOptions: (jobType: JobType) => Promise<ChartColumnEnum[]>;
  getFilteringOptions: (jobType: JobType) => Promise<ChartColumnEnum[]>;
  createChart: (parameters: UserDefinedChartParameters) => Promise<void>;
  highlightChart: (chartId: number) => void;
  updatedChartId: number;
}


export const JobStastiticsContext = createContext<Partial<JobStatisticsContextProps>>({});

export const JobStatisticsContextProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const [predefinedCharts, setPredefinedCharts] = useState<PredefinedJobChartStorage>(new Map<JobType, PreDefinedJobStatisticsChart[]>());
  const [userDefinedCharts, setUserDefinedCharts] = useState<UserDefinedJobChartStorage>(new Map<number, UserDefinedChartModel>());
  const [updatedChartId, setUpdatedChartId] = useState<number>();
  const updateType = useRef<"refresh" | "create">(null);
  const jobPerformanceService = useRef(new JobPerformanceService()).current;

  useEffect(() => {
    refreshAllPredefinedCharts();
  }, []);

  useEffect(() => {
    jobPerformanceService.getAvailableCharts().then((response) => {
      const newUserDefinedChartStorage = new Map(userDefinedCharts);
      Promise.all(response.map((metadata) => {
        return jobPerformanceService.getChartData(metadata.chartId).then((dataPoints) => {
          newUserDefinedChartStorage.set(metadata.chartId, { dataPoints, metadata });
        }, (error) => {
          metadata.errorMessage = error.responseText;
          newUserDefinedChartStorage.set(metadata.chartId, { dataPoints: [], metadata });
        });
      })).then(() => {
        const sorted = new Map(sortBy([...newUserDefinedChartStorage.entries()], (entry) => entry[0], "asc"));
        setUserDefinedCharts(sorted);
      });
    })
  }, []);

  const refreshAllPredefinedCharts = () => {
    const promises: Array<Promise<(PreDefinedJobStatisticsChart)[]>> = [
      jobPerformanceService.getChartsByJobAndChartType(GraphTypeEnum.Runtime).then<PreDefinedJobStatisticsChart[]>((apiResponse) => {
        return apiResponse.map((r) => ({ ...r, chartType: GraphTypeEnum.Runtime }));
      }),
      jobPerformanceService.getChartsByJobAndChartType(GraphTypeEnum.MaxCPU).then((apiResponse) => {
        return apiResponse.map((r) => ({ ...r, chartType: GraphTypeEnum.MaxCPU }));
      }),
      jobPerformanceService.getChartsByJobAndChartType(GraphTypeEnum.MaxRAM).then((apiResponse) => {
        return apiResponse.map((r) => ({ ...r, chartType: GraphTypeEnum.MaxRAM }));
      }),
      jobPerformanceService.getChartsByJobAndChartType(GraphTypeEnum.Runs).then((apiResponse) => {
        return apiResponse.map((r) => ({ ...r, chartType: GraphTypeEnum.Runs }));
      }),
      jobPerformanceService.getJobCountChart().then((apiResponse) => {
        return apiResponse.map((r) => ({ dataPoints: [r] }))
      }),
    ];
    Promise.all(promises).then((responses) => {
      // responses are in the same order as the requests
      const graphStorage = new Map<JobType | "Job Count", PreDefinedJobStatisticsChart[]>();
      responses.forEach((response) => {
        if (response.some((r) => !!(r as PreDefinedJobStatisticsChart).jobType)) {
          response.forEach((graph) => {
            const currentGraphs = graphStorage.get((graph as PreDefinedJobStatisticsChart).jobType) || [];
            graphStorage.set((graph as PreDefinedJobStatisticsChart).jobType, currentGraphs.concat(graph as PreDefinedJobStatisticsChart));
          });
        } else {
          // job count charts don't have a job type from the API
          const jobChart: PreDefinedJobStatisticsChart = {
            chartType: GraphTypeEnum.Unknown,
            dataPoints: (response).map((r: PreDefinedJobStatisticsChart) => ({ xAxisValue: r.dataPoints[0].xAxisValue, yAxisValue: r.dataPoints[0].yAxisValue })),
            jobType: null,
          };
          graphStorage.set("Job Count", [jobChart]);
        }
      });
      setPredefinedCharts(graphStorage);
    });
  }

  const refreshPredefinedChart = (jobType: JobType, graphType: GraphTypeEnum) => {
    if (!!jobType) {
      jobPerformanceService.getChartsByJobAndChartType(graphType, [jobType]).then((refresh) => {
        // should only be a single graph
        refresh[0].chartType = graphType;
        const newStorage = new Map(predefinedCharts);
        const currentJobStorage = newStorage.get(jobType);
        const indexToReplace = currentJobStorage.findIndex((graph) => graph.chartType === graphType);
        currentJobStorage.splice(indexToReplace, 1, refresh[0]);
        newStorage.set(jobType, currentJobStorage);
        setPredefinedCharts(newStorage);
      });
    } else {
      jobPerformanceService.getJobCountChart().then((response) => {
        const newChart: PreDefinedJobStatisticsChart = {
          chartType: GraphTypeEnum.Unknown,
          dataPoints: response,
        };
        const newStorage = new Map(predefinedCharts);
        newStorage.set("Job Count", [newChart]);
        setPredefinedCharts(newStorage);
      });
    }
  }

  const refreshUserDefinedChart = (chartId: number, highlight?: boolean) => {
    const newUserDefinedChartStorage = new Map(userDefinedCharts);
    jobPerformanceService.refreshUserDefinedChart(chartId).then((data) => {
      const currentEntry = newUserDefinedChartStorage.get(chartId);
      newUserDefinedChartStorage.set(chartId, { dataPoints: data, metadata: currentEntry.metadata });
      if (highlight) {
        setUpdatedChartId(chartId);
        updateType.current = "refresh";
      }
      setUserDefinedCharts(newUserDefinedChartStorage);
    });
  }

  const deleteUserDefinedChart = (chartId: number) => {
    jobPerformanceService.deleteChart(chartId);
    const newUserDefinedChartStorage = new Map(userDefinedCharts);
    newUserDefinedChartStorage.delete(chartId);
    setUserDefinedCharts(newUserDefinedChartStorage);
  }

  const getJobTypes = async () => {
    return await jobPerformanceService.getJobTypes();
  }

  const getXAxisOptions = async (jobType: JobType) => {
    return await jobPerformanceService.getXAxisOptions(jobType);
  }

  const getFilteringOptions = async (jobType: JobType) => {
    return await jobPerformanceService.getFilteringOptions(jobType);
  }

  const createChart = async (parameters: UserDefinedChartParameters) => {
    const newMetadata = await jobPerformanceService.createChart(parameters);
    const chartAlreadyExists = Array.from(userDefinedCharts.keys()).find((c) => c === newMetadata.chartId);
    if (chartAlreadyExists) {
      // if the chart already exists, refresh it and highlight it for the user
      refreshUserDefinedChart(newMetadata.chartId, true);
    } else {
      const newUserDefinedChartStorage = new Map(userDefinedCharts);
      // if it doesn't, fetch the datapoints and add it to storage
      return jobPerformanceService.getChartData(newMetadata.chartId).then((dataPoints) => {
        newUserDefinedChartStorage.set(newMetadata.chartId, { dataPoints, metadata: newMetadata });
        // newChartId.current = newMetadata.chartId;
        setUpdatedChartId(newMetadata.chartId);
        updateType.current = "create";
        setUserDefinedCharts(newUserDefinedChartStorage);
      }, (error) => {
        newMetadata.errorMessage = error.responseText;
        newUserDefinedChartStorage.set(newMetadata.chartId, { dataPoints: [], metadata: newMetadata });
      });
    }
  }

  const renderHighlightToast = () => {
    if (updateType.current === "refresh") {
      toast("You already have a chart with these specifications.", {
        containerId: "z-index-1030",
        type: "info",
      });
    } else if (updateType.current === "create") {
      toast("Your chart has successfully been created.", {
        containerId: "z-index-1030",
        type: "success",
      });
    }
  }

  /* Highlighting is a little weird. 
   * When a "collapse" section is closed, its children are removed from the DOM. This is done because they don't really provide any accessibility perks and they have a lot of DOM nodes that slow down the page.
   * But because they're not in the DOM, we have to ensure that section is open before we can target the chart.
   * This context doesn't have access to that context. So we have to update state, let that pass down to JobStatistics as props, and let it handle it because it has access to both contexts.
   */
  const highlightChart = (chartId: number) => {
    renderHighlightToast();
    const chartContainer = document.querySelector(`.job-stats__chart-container[data-id="${chartId}"]`);
    window.scrollTo({ behavior: "smooth", left: 0, top: chartContainer.getBoundingClientRect().top - 150 });
    chartContainer.classList.add("highlight");
    const highlightFadeTimer = window.setTimeout(() => {
      chartContainer.classList.remove("highlight");
      chartContainer.classList.add("highlight-outer");
    }, 1500);
    // I'd prefer mouseOver here, but it doesn't work. I'm guessing recharts prevents mouseovers from bubbling up
    chartContainer.addEventListener("click", (e) => {
      (e.target as Element).closest(".job-stats__chart-container").classList.remove("highlight");
      (e.target as Element).closest(".job-stats__chart-container").classList.remove("highlight-outer");
      window.clearTimeout(highlightFadeTimer);
    }, { once: true });

    setUpdatedChartId(null);
    updateType.current = null;
  }

  return (
    <JobStastiticsContext.Provider
      value={{
        createChart: createChart,
        deleteUserDefinedChart: deleteUserDefinedChart,
        getFilteringOptions: getFilteringOptions,
        getJobTypes: getJobTypes,
        getXAxisOptions: getXAxisOptions,
        highlightChart: highlightChart,
        predefinedCharts: predefinedCharts,
        refreshAllPredefinedCharts: refreshAllPredefinedCharts,
        refreshPredefinedChart: refreshPredefinedChart,
        refreshUserDefinedChart: refreshUserDefinedChart,
        updatedChartId: updatedChartId,
        userDefinedCharts: userDefinedCharts,
      }}
    >
      {children}
    </JobStastiticsContext.Provider>
  );
};
